import React from 'react';
import { X, ShieldX, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

interface VerificationRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VerificationRequiredModal: React.FC<VerificationRequiredModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Content */}
        <div className="p-6 text-center">
          {/* Icon */}
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldX className="w-8 h-8 text-yellow-600" />
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Skills Verification Required
          </h2>

          {/* Message */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            To ensure quality and maintain standards, only verified candidates can attend tests. 
            Please complete your skills verification first to unlock all available tests.
          </p>

          {/* Benefits */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
              <Star className="w-4 h-4 mr-2" />
              Why Verify Your Skills?
            </h3>
            <ul className="text-blue-800 text-sm space-y-2">
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                Access all available job tests
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                Get verified badge on your profile
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                Stand out to employers
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                Quick 20-question assessment
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/job-seeker/skills-validation"
              className="flex-1 flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              onClick={onClose}
            >
              <Star className="w-4 h-4 mr-2" />
              Verify Skills Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Maybe Later
            </button>
          </div>

          {/* Additional Note */}
          <p className="text-xs text-gray-500 mt-4">
            Need 60% or higher score to get verified
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationRequiredModal;